<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-user" class="q-mb-sm" /> {{ $t('users') }}
		</div>

		<filters
			class="filter-card"
			:options="getOptionsFilter"
			@requestFiltered="requestFiltered"
			@clearAndRequest="clearAndRequest"
		/>

		<q-table
			:data="users"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					:debounce="600"
					icon="far fa-search"
					:placeholder="$t('search')"
				/>

				<div class="col" />
				<q-btn
					color="secondary"
					v-if="reportsPermission"
					icon="fas fa-download"
					class="q-mr-sm"
					:label="$t('user_reports')"
					@click="downloadReport()"
				/>
				<q-btn
					color="secondary"
					:disable="managerUser"
					icon="far fa-plus"
					:label="$t('new')"
					@click="$router.push({ name: 'admin_user', params: { id: 0 } })"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props" v-show="col.show">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props" v-show="showColumn(props.colsMap, 'id')">
					{{ props.row.id }}
				</q-td>

				<q-td
					key="full_name"
					:props="props"
					v-show="showColumn(props.colsMap, 'full_name')"
				>
					{{ props.row.full_name }}
				</q-td>

				<q-td key="gender" :props="props" v-show="showColumn(props.colsMap, 'gender')">
					{{ $t(props.row.gender) }}
				</q-td>

				<q-td key="document" :props="props" v-show="showColumn(props.colsMap, 'document')">
					{{ props.row.document }}
				</q-td>

				<q-td key="email" :props="props" v-show="showColumn(props.colsMap, 'email')">
					{{ props.row.email }}
				</q-td>

				<q-td key="birthday" :props="props" v-show="showColumn(props.colsMap, 'birthday')">
					{{ props.row.birthday }}
				</q-td>

				<q-td
					key="date_joined"
					:props="props"
					v-show="showColumn(props.colsMap, 'date_joined')"
				>
					{{ props.row.date_joined }}
				</q-td>

				<q-td
					key="date_updated"
					:props="props"
					v-show="showColumn(props.colsMap, 'date_updated')"
				>
					{{ props.row.date_updated }}
				</q-td>

				<q-td key="profile" :props="props" v-show="showColumn(props.colsMap, 'profile')">
					{{ translateProfiles(props.row.profile) }}
				</q-td>

				<q-td
					key="main_area"
					:props="props"
					v-show="
						current_client === 'Chevrolet' && showColumn(props.colsMap, 'main_area')
					"
				>
					{{ getMainArea(props.row.main_area) }}
				</q-td>

				<q-td key="register" :props="props" v-show="showColumn(props.colsMap, 'register')">
					{{ props.row.register }}
				</q-td>

				<q-td
					key="companies"
					:props="props"
					v-show="showColumn(props.colsMap, 'companies')"
				>
					<template v-if="props.row.companies.length === 1">
						{{
							`${props.row.companies[0].name} ${
								props.row.companies[0].document ? '- ' : ''
							} ${
								props.row.companies[0].document
									? props.row.companies[0].document
									: ''
							}  ${props.row.companies[0].register ? '- ' : ''} ${
								props.row.companies[0].register
									? props.row.companies[0].register
									: ''
							}`
						}}
					</template>

					<template v-if="props.row.companies.length > 1">
						<q-btn
							color="primary"
							text-color="white"
							:label="props.row.companies.length"
							no-ripple
						>
							<q-tooltip>
								<p class="tooltip q-mb-sm">
									<span
										v-for="(branch, index) in props.row.companies"
										:key="index"
									>
										{{
											`${branch.name} ${branch.document ? '- ' : ''} ${
												branch.document ? branch.document : ''
											}  ${branch.register ? '- ' : ''} ${
												branch.register ? branch.register : ''
											}`
										}}
										<br v-if="props.row.companies.length - 1 > index" />
									</span>
								</p>
							</q-tooltip>
						</q-btn>
					</template>
				</q-td>

				<q-td key="branches" :props="props" v-show="showColumn(props.colsMap, 'branches')">
					<template v-if="props.row.branches.length === 1">
						{{
							`${props.row.branches[0].name} ${
								props.row.branches[0].document ? '- ' : ''
							} ${
								props.row.branches[0].document ? props.row.branches[0].document : ''
							}  ${props.row.branches[0].register ? '- ' : ''} ${
								props.row.branches[0].register ? props.row.branches[0].register : ''
							}`
						}}
					</template>

					<template v-if="props.row.branches.length > 1">
						<q-btn
							color="primary"
							text-color="white"
							:label="props.row.branches.length"
							no-ripple
						>
							<q-tooltip>
								<p class="tooltip q-mb-sm">
									<span
										v-for="(branch, index) in props.row.branches"
										:key="index"
									>
										{{
											`${branch.name} ${branch.document ? '- ' : ''} ${
												branch.document ? branch.document : ''
											}  ${branch.register ? '- ' : ''} ${
												branch.register ? branch.register : ''
											}`
										}}
										<br v-if="props.row.branches.length - 1 > index" />
									</span>
								</p>
							</q-tooltip>
						</q-btn>
					</template>
				</q-td>

				<q-td
					key="accept_terms_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'accept_terms_date')"
				>
					{{ props.row.accept_terms_date }}
				</q-td>

				<q-td
					key="status"
					:props="props"
					auto-width
					v-show="showColumn(props.colsMap, 'status')"
				>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatus(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props" v-show="showColumn(props.colsMap, 'actions')">
					<q-btn
						flat
						round
						color="secondary"
						icon="far fa-id-card"
						@click="showReport(props.row)"
					>
						<q-tooltip>
							{{ $t('view_profile_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="primary"
						icon="far fa-pencil-alt"
						@click="
							$router.push({ name: 'admin_user', params: { id: props.row.actions } })
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						:disable="managerUser"
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						:disable="managerUser || checkGeneratedEmail(props.row)"
						round
						color="info"
						icon="fal fa-key"
						@click="confirmResetPassword(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('reset_password_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>

			<q-tr slot="bottom-row" slot-scope="props" :props="props">
				<q-td colspan="100%">
					<q-btn
						flat
						round
						dense
						color="secondary"
						size="small"
						icon="far fa-cog"
						@click="show_modal = true"
					>
						<q-tooltip>
							{{ $t('configure_table_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-modal
			v-model="show_modal"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="saveColumns()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('personalize_columns') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-md">
						<div
							class="col-xs-12 col-sm-4"
							v-for="(column, index) in columns"
							:key="index"
							v-if="column.label && column.label != '#'"
						>
							<q-checkbox v-model="column.show" :label="$t(column.label)" />
						</div>
					</div>

					<div class="row reverse gutter-md q-mt-md">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								icon="fal fa-times"
								color="primary"
								class="fit"
								:label="$t('close')"
								@click="saveColumns()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_user') }}</span>

			<span slot="message">{{ $t('confirm_remove_user') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="hideReport()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ this.selected_user.full_name }} - {{ this.selected_user.email }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-sm">
						<div class="col-12">
							<reports :clear_data="clear_data" :show_report="show_report" />
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report_users"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn
						flat
						round
						dense
						@click="show_report_users = false"
						icon="fas fa-times"
					/>
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div
						style="position: relative; width: 100%; height: 0px; padding-bottom: 56.25%"
					>
						<iframe
							style="position: absolute; left: 0; top: 0; width: 100%; height: 100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="confirm_reset_password" @ok="resetPassword">
			<span slot="title">{{ $t('reset_password') }}</span>

			<span slot="message">{{ $t('confirm_send_reset_password_to_user_email') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn
					color="primary"
					:label="$t('cancel')"
					@click="confirm_reset_password = false"
				/>
				<q-btn color="negative" :label="$t('reset_password')" @click="resetPassword()" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_success">
			<span slot="title">{{ $t('reset_password') }}</span>

			<span slot="message">{{
				$t('an_email_was_sent_to_the_user_with_instructions_to_reset_his_password')
			}}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('ok')" @click="show_success = false" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_report_problem" prevent-close>
			<span slot="title">{{ $t('report_download_failed') }}</span>

			<div slot="message">
				<span class="q-pa-xs">{{ $t('report_download_failed_exceeded_time') }}</span>
				<p class="q-pa-xs">{{ $t('the_report_it_will_be_sent_to_your_email') }}</p>
			</div>

			<template slot="buttons" slot-scope="props" :props="props" class="justify-center">
				<div class="row full-width q-mb-xs">
					<q-btn
						color="positive"
						class="offset-4 col-4"
						:label="$t('ok')"
						@click="show_report_problem = false"
					/>
				</div>
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { LocalStorage } from 'quasar'
import { date } from 'quasar'
const { formatDate } = date
import Filters from 'components/admin/Filters.vue'
import Reports from 'components/admin/users/Reports.vue'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'users', 'feed', 'imports'] },

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			users: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
					show: true,
				},
				{
					name: 'full_name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'full_name',
					sortable: true,
					show: true,
				},
				{
					name: 'gender',
					required: true,
					label: 'gender',
					align: 'left',
					field: 'gender',
					sortable: false,
					show: false,
				},
				{
					name: 'document',
					required: true,
					label: 'document',
					align: 'left',
					field: 'document',
					sortable: false,
					show: false,
				},
				{
					name: 'email',
					required: true,
					label: 'email',
					align: 'left',
					field: 'email',
					sortable: true,
					show: true,
				},
				{
					name: 'birthday',
					required: true,
					label: 'birthday',
					align: 'left',
					field: 'birthday',
					sortable: false,
					show: false,
				},
				{
					name: 'date_joined',
					required: true,
					label: 'date_joined',
					align: 'left',
					field: 'date_joined',
					sortable: false,
					show: false,
				},
				{
					name: 'date_updated',
					required: true,
					label: 'date_updated',
					align: 'left',
					field: 'date_updated',
					sortable: false,
					show: false,
				},
				{
					name: 'profile',
					required: true,
					label: 'profile',
					align: 'left',
					field: 'profile',
					sortable: true,
					show: true,
				},
				{
					name: 'main_area',
					required: true,
					label: 'main_area',
					align: 'left',
					field: 'main_area',
					sortable: false,
					show: false,
				},
				{
					name: 'register',
					required: true,
					label: 'register',
					align: 'left',
					field: 'register',
					sortable: false,
					show: false,
				},
				{
					name: 'companies',
					required: true,
					label: 'companies_or_chains',
					align: 'left',
					field: 'companies',
					sortable: false,
					show: false,
				},
				{
					name: 'branches',
					required: true,
					label: 'branch',
					align: 'left',
					field: 'branches',
					sortable: false,
					show: true,
				},
				{
					name: 'accept_terms_date',
					required: false,
					label: 'accept_terms_date',
					align: 'left',
					field: 'accept_terms_date',
					sortable: false,
					show: false,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
					show: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
					show: true,
				},
			],
			filter: '',
			show_modal: false,
			action_id: '',
			confirm_remove: false,
			show_report: false,
			clear_data: false,
			selected_user: {},
			show_report_users: false,
			report_url: '',
			dropdown_reports: false,
			confirm_reset_password: false,
			show_success: false,
			show_report_problem: false,
			profile: [],
			status: [],
			areas: [],
			companies: [],
			company_branches: [],
			current_client: '',
		}
	},

	components: {
		reports: Reports,
		filters: Filters,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			user_clients: 'common/user_clients',
			id: 'common/id',
			profiles: 'registration_lists/profiles',
			reports: 'registration_lists/reports',
			user_client_module_items: 'common/user_client_module_items',
			id_user_client_selected: 'common/id_user_client_selected',
			num_rows: 'datatable/num_rows',
			users_columns: 'datatable_columns/users_columns',
			login_default_language_id: 'params/login_default_language_id',
			languages: 'params/languages',
			id_user_language_selected: 'common/id_user_language_selected',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
		}),

		getOptionsFilter() {
			let client = this.user_clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (client) {
				return {
					user_areas: client.client_areas ? client.client_areas : [],
					profiles: client.client_profiles ? client.client_profiles : [],
					statuses:
						this.current_client === 'Chevrolet'
							? [
									{ value: 1, label: this.$t('active') },
									{ value: 3, label: this.$t('deleted') },
									{ value: 2, label: this.$t('inactive') },
									{ value: 9, label: this.$t('inactive_license') },
									{ value: 10, label: this.$t('inactive_former_employee') },
									{ value: 11, label: this.$t('inactive_compliance') },
									{ value: 12, label: this.$t('inactive_vacation') },
							  ]
							: [
									{ value: 1, label: this.$t('active') },
									{ value: 2, label: this.$t('inactive') },
									{ value: 4, label: this.$t('awaiting_validation') },
									{ value: 5, label: this.$t('active_web_service') },
									{ value: 6, label: this.$t('self_registered') },
									{ value: 7, label: this.$t('inactive_web_service') },
									{ value: 8, label: this.$t('blocked') },
									{ value: 9, label: this.$t('inactive_license') },
									{ value: 10, label: this.$t('inactive_former_employee') },
									{ value: 11, label: this.$t('inactive_compliance') },
									{ value: 12, label: this.$t('inactive_vacation') },
							  ],
					has_filter_store: true,
					has_filter_companies: true,
				}
			}
		},

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		filteredReports() {
			let obj = this.reports.filter((obj) => {
				return (
					obj.slug_type === 'users' &&
					obj.url != 'https://www.take5.com.br' &&
					obj.url != ''
				)
			})
			return obj
		},

		managerUser() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'users_manage'
				})
				if (item.permission) return false
				else return true
			}
			return true
		},

		reportsPermission() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'reports'
				})
				if (item) {
					return item.permission
				}
			}
			return false
		},

		hasReports() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'reports_panel'
				})
				if (item) {
					return item.permission
				}
			}
			return false
		},
	},

	methods: {
		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				status: this.status,
				profile: this.profile,
				areas: this.areas,
				companies: this.companies,
				company_branches: this.company_branches,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/admin_users/users/?${this.$qs.stringify(data, { arrayFormat: 'repeat' })}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.users = response.data.results.map((e) => {
						return {
							...e,
							main_area: e.main_areas && e.main_areas.length ? e.main_areas[0] : '',
							birthday: e.birthday ? date.formatDate(e.birthday, 'DD/MM/YYYY') : '',
							date_joined: e.date_joined
								? date.formatDate(e.date_joined, 'DD/MM/YYYY HH:mm:ss')
								: '',
							date_updated: e.date_updated
								? date.formatDate(e.date_updated, 'DD/MM/YYYY HH:mm:ss')
								: '',
							accept_terms_date: e.accept_terms_date
								? moment(e.accept_terms_date)
										.tz('Etc/GMT')
										.format('DD/MM/YYYY - HH:mm')
								: '',
							actions: e.id,
						}
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_users/users/${this.action_id}/`, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('user_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		confirmResetPassword(id) {
			this.action_id = id
			this.confirm_reset_password = true
		},

		resetPassword() {
			this.$q.loading.show()
			this.confirm_reset_password = false

			const user = this.users.find((obj) => obj.id === this.action_id)

			const language = this.languages.find(
				(l) => l.code.split('-')[0] === this.$i18n.i18next.language
			)

			let fd = new FormData()
			fd.append('user_email', user.email)
			fd.append('language', language ? language.id : this.login_default_language_id)

			this.$axios
				.post('/etraining_login/api_recover_password/', fd, this.getHeader)
				.then(() => {
					this.show_success = true
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		getStatus(status) {
			if (status == 1) return 'active'
			else if (status == 2) return 'inactive'
			else if (status == 3) return 'deleted'
			else if (status == 4) return 'awaiting_validation'
			else if (status == 5) return 'active_web_service'
			else if (status == 6) return 'self_registered'
			else if (status == 7) return 'inactive_web_service'
			else if (status == 8) return 'blocked'
			else if (status == 9) return 'inactive_license'
			else if (status == 10) return 'inactive_former_employee'
			else if (status == 11) return 'inactive_compliance'
			else if (status == 12) return 'inactive_vacation'
			else if (status == 13) return 'exclude_data'
			else if (status == 14) return 'anonymized_data'
			else if (status == 15) return 'inactive_auto'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
			else if (status == 4) return 'fas fa-exclamation-triangle'
			else if (status == 5) return 'fab fa-internet-explorer'
			else if (status == 6) return 'fas fa-portrait'
			else if (status == 7) return 'fas fa-ban'
			else if (status == 8) return 'fas fa-ban'
			else if (status == 9) return 'fal fa-house-leave'
			else if (status == 10) return 'fas fa-ban'
			else if (status == 11) return 'fas fa-ban'
			else if (status == 12) return 'fal fa-island-tropical'
			else if (status == 13) return 'fas fa-exclamation-triangle'
			else if (status == 14) return 'fas fa-user-secret'
			else if (status == 15) return 'fas fa-ban'
		},

		getColorStatus(status) {
			if (status == 1 || status == 5) return 'positive'
			else if (status == 3 || status == 8 || status == 10) return 'negative'
			else if (
				status == 2 ||
				status == 4 ||
				status == 7 ||
				status == 9 ||
				status == 11 ||
				status == 12 ||
				status == 13 ||
				status == 15
			)
				return 'warning'
			else if (status == 6 || status == 14) return 'info'
		},

		getProfile(id) {
			let profile = this.profiles.find((obj) => {
				return obj.value === id
			})
			if (profile) return profile.label
			else return ''
		},

		showReport(user) {
			this.$store.dispatch('users/SET_SELECTED_USER', [user.id])
			this.selected_user = user
			this.show_report = true
		},

		hideReport() {
			this.clear_data = !this.clear_data
			this.show_report = false
			this.loadPagination()
		},

		showColumn(list, name) {
			if (list[name]) return list[name].show
			else return false
		},

		saveColumns() {
			this.show_modal = false
			this.saveDatatableColumns()
		},

		showReportUsers(report) {
			this.report_url = report.url
			this.dropdown_reports = false
			this.show_report_users = true
		},

		requestFiltered(field) {
			this.status = field.status
			this.profile = field.profile
			;(this.areas = field.user_area),
				(this.companies = field.companies),
				(this.company_branches = field.company_branches)

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: 5,
					descending: false,
					sortBy: null,
				},
			})
		},

		clearAndRequest() {
			this.status = []
			this.profile = []
			this.areas = []
			this.companies = []
			this.company_branches = []

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		saveDatatableColumns() {
			this.$store.dispatch('datatable_columns/SET_USERS_COLUMNS', [this.columns])
		},

		loadDatatableColumns() {
			if (process.env.PROJECT_FOLDER !== 'universidadechevrolet') {
				this.columns = this.columns.filter((c) => c.name !== 'main_area')

				const storedCol = this.users_columns.find((c) => c.name === 'main_area')
				if (storedCol) {
					this.$store.dispatch('datatable_columns/SET_USERS_COLUMNS', [this.columns])
				}
			}
			if (this.$compareStoredColumns(this.columns, this.users_columns))
				this.columns = this.users_columns
			else return
		},

		downloadReport() {
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})

			let data = {
				search: this.filter,
				status: this.status,
				profile: this.profile,
				areas: this.areas,
				companies: this.companies,
				company_branches: this.company_branches,
			}

			this.$axios
				.get(
					`admin_users/users/generate_report/?${this.$qs.stringify(data, {
						arrayFormat: 'repeat',
					})}`,
					this.getHeader
				)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					this.show_report_problem = true
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		translateProfiles(value) {
			let profile = value.toUpperCase()
			let term = this.profiles.find((e) => e.translations[0].name.toUpperCase() === profile)
			if (term) {
				let translated = term.label.toLowerCase()
				return translated.charAt(0).toUpperCase() + translated.slice(1)
			} else return value
		},

		getClientName(id) {
			let obj = this.user_clients.find((obj) => {
				return obj.id === id
			})
			if (obj) return obj.name
			else return ''
		},

		enableCustomClientCheck(client) {
			this.current_client = this.getClientName(client)
		},

		chevroletDocumentFix() {
			if (
				process.env.PROJECT_FOLDER === 'universidadechevrolet' &&
				this.id_user_client_country_selected === 32 &&
				this.id_user_language_selected == 1
			) {
				this.columns = this.columns.map((e) =>
					e.field == 'document' ? { ...e, label: 'cpf' } : e
				)
			} else {
				this.columns = this.columns.map((e) =>
					e.field == 'document' ? { ...e, label: 'document' } : e
				)
			}
		},

		getMainArea(area) {
			let client = this.user_clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (client) {
				let obj = client.client_areas.find((obj) => {
					return obj.value === area
				})
				if (obj) return obj.label
				else return ''
			} else return ''
		},

		checkGeneratedEmail({ email, document }) {
			if (document) {
				return email.split('@')[0].replace(/[^\d]+/g, '') == document
			}
		},
	},

	watch: {
		id_user_language_selected: function() {
			this.chevroletDocumentFix()
		},
	},

	created() {
		this.loadDatatableColumns()
		this.loadPagination()

		this.fetchLists()
		this.enableCustomClientCheck(this.id_user_client_selected)

		this.chevroletDocumentFix()
	},
}
</script>

<style lang="stylus" scoped>
.filter-card
    border-radius 4px
    box-shadow 0 1px 2px rgba(0,0,0,0.2), 0 1px 1px rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12)
</style>
